import { useState, useMemo } from 'react';

import type { Snippet } from '../snippetTypes.ts';
import { DEFAULT_CATEGORY_ID, SNIPPET_CATEGORY_IDS } from '../constants/snippetCategories.ts';

/**
 * Pure function to filter snippets based on category and search query.
 * Extracted for testability and reuse.
 */
export const filterSnippets = (
	snippets: Snippet[],
	categoryId: string,
	searchQuery: string,
	currentUserId?: string | null,
): Snippet[] => {
	let categoryFiltered = snippets;

	// Apply category filter
	if (categoryId === SNIPPET_CATEGORY_IDS.MY_TEMPLATES) {
		categoryFiltered = snippets.filter((snippet) => {
			if (currentUserId) {
				return snippet.createdBy.accountId.toLowerCase() === currentUserId.toLowerCase();
			}
			return false;
		});
	} else if (categoryId === SNIPPET_CATEGORY_IDS.FAVORITES) {
		categoryFiltered = snippets.filter((snippet) => snippet?.isFavorite);
	}

	// Apply search filter
	const trimmedSearchQuery = searchQuery.trim().toLowerCase();
	if (!trimmedSearchQuery) {
		return categoryFiltered;
	}

	return categoryFiltered.filter((snippet) => {
		const nameMatch = snippet.name.toLowerCase().includes(trimmedSearchQuery);
		const ownerMatch = snippet.createdBy.displayName.toLowerCase().includes(trimmedSearchQuery);
		const descriptionMatch = snippet.description?.toLowerCase().includes(trimmedSearchQuery);
		return nameMatch || ownerMatch || descriptionMatch;
	});
};

/**
 * Hook for managing snippet filter state locally.
 *
 * This hook manages category and search query filters independently for each component,
 * preventing filter state conflicts when multiple pages/components use snippets.
 *
 * @param snippets - Array of snippets to filter
 * @param currentUserId - Optional user ID for MY_TEMPLATES filter
 * @returns Object containing:
 *   - selectedCategoryId: Current category filter
 *   - searchQuery: Current search query
 *   - filteredSnippets: Snippets filtered by current filters
 *   - setCategoryFilter: Set the category filter
 *   - setSearchQuery: Set the search query
 *   - resetFilters: Reset filters to defaults
 */
export const useSnippetFilters = (snippets: Snippet[], currentUserId?: string | null) => {
	const [selectedCategoryId, setSelectedCategoryId] =
		useState<SNIPPET_CATEGORY_IDS>(DEFAULT_CATEGORY_ID);
	const [searchQuery, setSearchQuery] = useState('');

	const filteredSnippets = useMemo(
		() => filterSnippets(snippets, selectedCategoryId, searchQuery, currentUserId),
		[snippets, selectedCategoryId, searchQuery, currentUserId],
	);

	return {
		selectedCategoryId,
		searchQuery,
		filteredSnippets,
		setCategoryFilter: setSelectedCategoryId,
		setSearchQuery,
		resetFilters: () => {
			setSelectedCategoryId(DEFAULT_CATEGORY_ID);
			setSearchQuery('');
		},
	};
};
